import { createContext, useContext, type ReactNode } from 'react';

/**
 * Runtime application configuration, loaded from the server at startup
 * via the /api/veterans-case-mgmt/v1/app-config.js script tag in index.html.
 *
 * These are public settings for the browser app. Do not include secrets.
 */
export interface AppConfig {
  readonly auth: {
    readonly clientId: string;
    readonly domain: string;
    readonly audience: string;
    readonly callbackPath: string;
  };
}

declare global {
  interface Window {
    opengovConfig?: AppConfig;
  }
}

const LOCAL_DEV_CONFIG: AppConfig = {
  auth: {
    clientId: 'local-dev',
    domain: 'localhost',
    audience: 'local',
    callbackPath: '/admin/auth-callback',
  },
};

const ConfigContext = createContext<AppConfig | null>(null);

export function ConfigProvider({ children }: { readonly children: ReactNode }) {
  const config = window.opengovConfig ?? LOCAL_DEV_CONFIG;

  return <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>;
}

export function useConfig(): AppConfig {
  const config = useContext(ConfigContext);
  if (!config) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return config;
}
